import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { X, Check, Search, ChevronDown, ChevronUp, Link2, ExternalLink, Users, Truck, Clock, Percent, BookImage, Package, Sparkles } from "lucide-react";

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

const score = (name, q) => {
  const a = (name || "").toLowerCase().trim(), b = (q || "").toLowerCase().trim();
  if (!a || !b) return 0;
  if (a === b) return 10;
  let sc = 0;
  if (a.startsWith(b) || b.startsWith(a)) sc += 4;
  else if (a.includes(b) || b.includes(a)) sc += 3;
  const at = a.split(/[^a-zà-ú0-9]+/).filter((x) => x.length > 2);
  const bt = b.split(/[^a-zà-ú0-9]+/).filter((x) => x.length > 2);
  bt.forEach((tk) => { if (at.includes(tk)) sc += 2; else if (at.some((x) => x.startsWith(tk))) sc += 1; });
  return sc;
};

export default function CustomMenuModal({ pages, sectionId, item, onSave, onClose }) {
  const { t } = useI18n();
  const [label, setLabel] = useState(item?.label || "");
  const [target, setTarget] = useState(item?.to ? { ttype: item.ttype, id: item.tid, name: item.tname, to: item.to } : null);
  const [data, setData] = useState(null);
  const [browse, setBrowse] = useState(false);
  const [openFolder, setOpenFolder] = useState(null);
  const [folderQ, setFolderQ] = useState("");

  const TYPES = useMemo(() => ({
    page: { label: t("cmTypePage"), icon: ExternalLink, to: (x) => x.to },
    client: { label: t("customer"), icon: Users, to: (x) => `/app/partners?hl=${x.id}` },
    supplier: { label: t("supplier"), icon: Truck, to: (x) => `/app/suppliers?hl=${x.id}` },
    employee: { label: t("cmTypeEmployee"), icon: Clock, to: (x) => `/app/staff?hl=${x.id}` },
    formula: { label: t("formulaTitle"), icon: Percent, to: (x) => `/app/formula?open=${x.id}` },
    catalog: { label: t("catalogLbl"), icon: BookImage, to: (x) => `/app/catalog?hl=${x.id}` },
    ingredient: { label: t("inventory"), icon: Package, to: (x) => `/app/inventory?hl=${x.id}` },
  }), [t]);

  useEffect(() => {
    Promise.all([
      api.partners().catch(() => []),
      api.employees().catch(() => []),
      api.formulaList().catch(() => []),
      api.recipes().catch(() => []),
      api.ingredients().catch(() => []),
    ]).then(([partners, employees, formulas, recipes, ingredients]) => setData({
      page: pages.map((p) => ({ id: p.to, name: p.label, to: p.to })),
      client: partners.filter((p) => p.kind !== "supplier").map((p) => ({ id: p.id, name: p.name })),
      supplier: partners.filter((p) => p.kind === "supplier").map((p) => ({ id: p.id, name: p.name })),
      employee: employees.map((e) => ({ id: e.id, name: e.name })),
      formula: formulas.map((f) => ({ id: f.id, name: f.name })),
      catalog: recipes.map((r) => ({ id: r.id, name: r.name })),
      ingredient: ingredients.map((i) => ({ id: i.id, name: i.name })),
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => {
    if (!data || !label.trim() || label.trim().length < 2) return [];
    const out = [];
    Object.entries(data).forEach(([ttype, list]) => list.forEach((x) => {
      const s = score(x.name, label);
      if (s > 0) out.push({ ttype, ...x, s });
    }));
    return out.sort((a, b) => b.s - a.s).slice(0, 7);
  }, [data, label]);

  const pick = (sug) => setTarget({ ttype: sug.ttype, id: sug.id, name: sug.name, to: TYPES[sug.ttype].to(sug) });

  const save = () => {
    if (!target) return;
    onSave(sectionId, {
      id: item?.id || newId(),
      label: label.trim() || target.name,
      to: target.to, ttype: target.ttype, tid: target.id, tname: target.name,
    });
  };

  const top = !target && suggestions.length > 0 ? suggestions[0] : null;
  const TopIcon = top ? TYPES[top.ttype].icon : null;
  const TgtIcon = target ? (TYPES[target.ttype]?.icon || Link2) : null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[150] flex items-center justify-center p-4" onClick={onClose} data-testid="cm-modal">
      <div className="bg-card text-foreground rounded-2xl shadow-2xl w-full max-w-lg p-5 fade-up max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><Link2 className="w-4 h-4 text-primary" /> {item ? t("cmEditItem") : t("cmNewItem")}</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="cm-close"><X className="w-4 h-4" /></button>
        </div>

        <label className="label mb-1 block">{t("cmLabelLbl")}</label>
        <input autoFocus className="field w-full mb-3" placeholder={t("cmLabelPh")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="cm-label-input" />

        {target ? (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 mb-3" data-testid="cm-selected-target">
            <TgtIcon className="w-4 h-4 text-emerald-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{target.name}</div>
              <div className="text-[11px] text-muted-foreground">{t("cmLinkedTo")} · {TYPES[target.ttype]?.label || target.ttype}</div>
            </div>
            <button onClick={() => setTarget(null)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="cm-clear-target"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <>
            {top && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl px-3 py-2.5 mb-2" data-testid="cm-top-suggestion">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1 text-sm">
                    {t("cmRecognized")} <b>{top.name}</b> — <span className="text-muted-foreground">{TYPES[top.ttype].label}</span>
                  </div>
                  <button onClick={() => pick(top)} className="btn-primary text-xs py-1.5 px-3 shrink-0" data-testid="cm-confirm-top">
                    <Check className="w-3.5 h-3.5" /> {t("cmConfirmLink")}
                  </button>
                </div>
              </div>
            )}
            {suggestions.length > 1 && (
              <div className="mb-2" data-testid="cm-other-options">
                <div className="text-[11px] label mb-1">{t("cmOtherOptions")}</div>
                <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                  {suggestions.slice(1).map((s, i) => {
                    const Icon = TYPES[s.ttype].icon;
                    return (
                      <button key={`${s.ttype}-${s.id}`} onClick={() => pick(s)} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-muted text-left" data-testid={`cm-sug-${i}`}>
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{s.name}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{TYPES[s.ttype].label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!top && label.trim().length >= 2 && data && (
              <div className="text-xs text-muted-foreground mb-2" data-testid="cm-no-match">{t("gsNoResults")}</div>
            )}
            {!label.trim() && <p className="text-xs text-muted-foreground mb-2">{t("cmNoTarget")}</p>}

            <button onClick={() => setBrowse(!browse)} className="btn-ghost text-xs w-full mb-2" data-testid="cm-browse-toggle">
              <Search className="w-3.5 h-3.5" /> {t("cmBrowseAll")} {browse ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {browse && data && (
              <div className="border border-border rounded-xl mb-2 overflow-hidden" data-testid="cm-browse-tree">
                {Object.entries(TYPES).map(([k, cfg]) => {
                  const list = data[k] || [];
                  const open = openFolder === k;
                  const fl = open && folderQ.trim() ? list.filter((x) => x.name.toLowerCase().includes(folderQ.trim().toLowerCase())) : list;
                  return (
                    <div key={k} className="border-b border-border/50 last:border-0">
                      <button onClick={() => { setOpenFolder(open ? null : k); setFolderQ(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted" data-testid={`cm-folder-${k}`}>
                        <cfg.icon className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 text-left font-medium">{cfg.label}</span>
                        <span className="text-[10px] text-muted-foreground">{list.length}</span>
                        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {open && (
                        <div className="px-3 pb-2">
                          {list.length > 8 && <input className="field w-full py-1 text-xs mb-1" placeholder={t("search")} value={folderQ} onChange={(e) => setFolderQ(e.target.value)} data-testid={`cm-folder-search-${k}`} />}
                          <div className="max-h-40 overflow-y-auto flex flex-col">
                            {fl.slice(0, 80).map((x) => (
                              <button key={x.id} onClick={() => pick({ ttype: k, ...x })} className="text-left text-sm px-2 py-1 rounded hover:bg-muted truncate" data-testid={`cm-browse-pick-${k}-${x.id}`}>{x.name}</button>
                            ))}
                            {fl.length === 0 && <span className="text-xs text-muted-foreground px-2 py-1">{t("empty")}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <button onClick={save} disabled={!target} className="btn-primary w-full disabled:opacity-40" data-testid="cm-save">
          <Check className="w-4 h-4" /> {t("cmSaveItem")}
        </button>
      </div>
    </div>,
    document.body
  );
}
